import { FC, useEffect, useState, type ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { Link } from '@savgroup-front-common/core/src/atoms/link';
import {
  SafeFormattedMessageWithoutSpread,
  safeFormattedIntlString,
} from '@savgroup-front-common/core/src/formatters';
import { getOrderDeliveryMethodMessage } from '@savgroup-front-common/core/src/formatters/getOrderDeliveryMethodMessage';
import { getOrderDocumentTypeMessage } from '@savgroup-front-common/core/src/formatters/getOrderDocumentTypeMessage';
import { Field } from '@savgroup-front-common/core/src/molecules/Field';
import { SummarySection } from '@savgroup-front-common/core/src/molecules/SummarySection/SummarySection';
import { ChevronRightIcon } from '@savgroup-front-common/core/src/protons/icons';
import { MessageType } from '@savgroup-front-common/types';

import { DOSSIER_RESPONSIVE_MAX_WIDTH_PX } from '../../../../pages/DossierPage.styles';
import { buildCommandePath } from '../../../../utils/dossierRoutes';
import { useGetFileSummaryNewUi } from '../../FileSummaryNewUi.hooks';
import { messages } from '../../messages';

import {
  $InlineField,
  $InlineFieldLabel,
  $InlineFieldValue,
  $ShowAllButton,
  $ShowAllButtonWrap,
} from './OrderSectionSummary.styles';

interface InlineSummaryFieldProps {
  label: MessageType;
  children: ReactNode;
}

const InlineSummaryField = ({ label, children }: InlineSummaryFieldProps) => (
  <$InlineField>
    <$InlineFieldLabel>
      <SafeFormattedMessageWithoutSpread message={label} />
    </$InlineFieldLabel>
    <$InlineFieldValue>{children}</$InlineFieldValue>
  </$InlineField>
);

export const OrderSectionSummary: FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { data } = useGetFileSummaryNewUi();
  const context = data?.context;
  const [isNarrowViewport, setIsNarrowViewport] = useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia(`(max-width: ${DOSSIER_RESPONSIVE_MAX_WIDTH_PX}px)`).matches
      : false,
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const mediaQuery = window.matchMedia(`(max-width: ${DOSSIER_RESPONSIVE_MAX_WIDTH_PX}px)`);
    const onChange = () => {
      setIsNarrowViewport(mediaQuery.matches);
    };

    onChange();
    mediaQuery.addEventListener('change', onChange);

    return () => {
      mediaQuery.removeEventListener('change', onChange);
    };
  }, []);

  if (!context?.orderId) {
    return <> </>;
  }

  const orderReference = data?.context?.orderReference;
  const purchaseDate = data?.context.purchaseDate;
  const url = buildCommandePath(context.orderId);

  if (isNarrowViewport) {
    return (
      <SummarySection title={messages.order} fluid>
        {orderReference && (
          <InlineSummaryField label={messages.orderReference}>{orderReference}</InlineSummaryField>
        )}
        {purchaseDate && (
          <InlineSummaryField label={messages.purchaseDate}>
            {intl.formatDate(purchaseDate, {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}
          </InlineSummaryField>
        )}
        {context.purchaseStoreName && (
          <InlineSummaryField label={messages.PurchaseStore}>
            {context.purchaseStoreName}
          </InlineSummaryField>
        )}
        <$ShowAllButtonWrap>
          <$ShowAllButton
            type="button"
            onClick={() => navigate(url)}
            data-testid="order-section-show-all"
          >
            <SafeFormattedMessageWithoutSpread message={messages.showAllOrder} />
            <ChevronRightIcon size="12px" color="currentColor" />
          </$ShowAllButton>
        </$ShowAllButtonWrap>
      </SummarySection>
    );
  }

  return (
    <SummarySection title={messages.order}>
      {orderReference && (
        <Field label={messages.orderReference}>
          <Link href={url}>{orderReference}</Link>
        </Field>
      )}
      {purchaseDate && (
        <Field
          label={messages.purchaseDate}
          copy={intl.formatDate(purchaseDate, {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })}
        >
          {intl.formatDate(purchaseDate, {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })}
        </Field>
      )}
      {context.purchaseStoreName && (
        <Field label={messages.PurchaseStore} copy={context.purchaseStoreName}>
          {context.purchaseStoreName}
        </Field>
      )}
      {context.marketPlace && (
        <Field label={messages.marketPlace} copy={context.marketPlace}>
          {context.marketPlace}
        </Field>
      )}
      {!context.marketPlace && context.sellerName && (
        <Field label={messages.sellerName} copy={context.sellerName}>
          {context.sellerName}
        </Field>
      )}

      {context.order?.orderDocumentType && (
        <Field
          label={messages.orderDocumentType}
          copy={safeFormattedIntlString(
            getOrderDocumentTypeMessage(context.order?.orderDocumentType),
          )}
        >
          {safeFormattedIntlString(
            getOrderDocumentTypeMessage(context.order?.orderDocumentType),
          )}
        </Field>
      )}
      {context.ownerProduct?.orderDeliveryMethod && (
        <Field
          label={messages.orderDeliveryMethod}
          copy={safeFormattedIntlString(
            getOrderDeliveryMethodMessage(context.ownerProduct?.orderDeliveryMethod),
          )}
        >
          {safeFormattedIntlString(
            getOrderDeliveryMethodMessage(context.ownerProduct?.orderDeliveryMethod),
          )}
        </Field>
      )}
    </SummarySection>
  );
};
